/**
 * Node.js (pure-JS) implementation of IImageCanvas / IImageCanvasLoader.
 *
 * Uses the inline PNG codec — no native dependencies.
 * JPEG decoding is not supported; embed JPEG directly via pdf.pdf.embedJpg().
 */

import { IImageCanvas, IImageCanvasLoader } from './imageCanvas';
import { decodePng, encodePng, isPng } from './imageCodec';

/** In-memory RGBA canvas backed by a Uint8Array. */
export class NodeImageCanvas implements IImageCanvas {
  constructor(
    public readonly width: number,
    public readonly height: number,
    private readonly _pixels: Uint8Array
  ) {}

  getRgba(): Uint8Array {
    return this._pixels;
  }

  withRgba(pixels: Uint8Array): IImageCanvas {
    return new NodeImageCanvas(this.width, this.height, new Uint8Array(pixels));
  }

  toPng(): Uint8Array {
    return encodePng(this.width, this.height, this._pixels);
  }
}

/** Image loader for Node.js environments. Supports PNG only (pure JS, no native deps). */
export class NodeImageCanvasLoader implements IImageCanvasLoader {
  async fromBytes(bytes: Uint8Array): Promise<IImageCanvas> {
    if (isPng(bytes)) {
      const { width, height, pixels } = decodePng(bytes);
      return new NodeImageCanvas(width, height, pixels);
    }
    throw new Error(
      'JPEG decoding is not supported in Node.js without native dependencies. ' +
        'Embed JPEG directly via pdf.pdf.embedJpg() instead of using RawImage.'
    );
  }

  async fromUrl(url: string): Promise<IImageCanvas> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch image from ${url}: ${response.statusText}`);
    return this.fromBytes(new Uint8Array(await response.arrayBuffer()));
  }

  async fromPath(path: string): Promise<IImageCanvas> {
    // Use indirect import to prevent esbuild from bundling fs/promises for browser targets.
    const { readFile } = await new Function('p', 'return import(p)')('fs/promises');
    return this.fromBytes(new Uint8Array(await readFile(path)));
  }

  async fromFile(_file: File): Promise<IImageCanvas> {
    throw new Error(
      'fromFile() is not supported in Node.js. Use fromPath() or fromBytes() instead.'
    );
  }
}
